import { COLUMN_SELECT_ID } from "@/constants";
import {
  FilterOperator,
  FilterType,
  isReadOnlyFilterType,
  processFilterValue,
} from "@/lib/filters";
import { formatBoolean } from "@/lib/utils/formatters";
import type { FilterValue } from "@/types/dataTableSettings";
import type {
  ColumnFiltersState,
  Table,
  VisibilityState,
} from "@tanstack/react-table";
import type { TFunction } from "i18next";

export interface ActiveFilterDescriptor {
  columnId: string;
  value: FilterValue;
  /** Translation key for column header, if defined as string on the column def */
  headerTranslationKey: string | undefined;
  /** Same as `EnumFilter` `translationKey` when the column filter is an enum (schema/custom meta). */
  filterValueTranslationKey: string | undefined;
  /** True when column is missing (stale storage) or not visible in the grid */
  hidden: boolean;
}

/**
 * True when a persisted column filter row currently narrows the dataset
 * (matches useCreateDataTableOptions committed-filter semantics).
 */
export const isCommittedFilterRowActive = (row: {
  id: string;
  value: unknown;
}): boolean => {
  const fv = row.value as FilterValue;
  if (isReadOnlyFilterType(fv.filter)) {
    return true;
  }
  return fv.value !== undefined;
};

export const getActiveCommittedRawFilterRows = (
  state: ColumnFiltersState,
): ColumnFiltersState => state.filter(isCommittedFilterRowActive);

/**
 * `EnumFilter` labels options as `t(\`${translationKey}.${optionValue}\`)`; the same key lives on
 * `meta.filter.props.translationKey` when the column uses that filter.
 */
export const readEnumFilterTranslationKey = (
  meta: unknown,
): string | undefined => {
  if (!meta || typeof meta !== "object" || !("filter" in meta)) {
    return undefined;
  }
  const filter = (meta as { filter?: unknown }).filter;
  if (!filter || typeof filter !== "object" || !("props" in filter)) {
    return undefined;
  }
  const props = (filter as { props?: unknown }).props;
  if (!props || typeof props !== "object" || !("translationKey" in props)) {
    return undefined;
  }
  const tk = (props as { translationKey?: unknown }).translationKey;
  return typeof tk === "string" ? tk : undefined;
};

export const isFilteredColumnHidden = <T>(
  table: Table<T>,
  columnId: string,
  mergedColumnVisibility?: VisibilityState,
): boolean => {
  const column = table.getColumn(columnId);
  if (!column) {
    return true;
  }
  if (mergedColumnVisibility !== undefined) {
    const visibilityFlag = mergedColumnVisibility[columnId];
    if (visibilityFlag === false) {
      return true;
    }
    if (visibilityFlag === true) {
      return false;
    }
  }
  return !column.getIsVisible();
};

/**
 * Builds descriptors for active committed filters, skipping the row-selection column.
 *
 * Pass **`mergedColumnVisibility`** (same object the table receives in **`state.columnVisibility`**)
 * so callers’ `useMemo(..., [table, rawFilters])` stay correct: `table` is a stable ref and does not
 * invalidate when only column visibility changes.
 */
export const buildActiveFilterDescriptors = <T>(
  table: Table<T>,
  rawFilters: ColumnFiltersState,
  mergedColumnVisibility?: VisibilityState,
): ActiveFilterDescriptor[] =>
  getActiveCommittedRawFilterRows(rawFilters)
    .filter((row) => row.id !== COLUMN_SELECT_ID)
    .map((row) => {
      const column = table.getColumn(row.id);
      const header = column?.columnDef.header;
      const headerTranslationKey =
        typeof header === "string" ? header : undefined;
      const hidden = isFilteredColumnHidden(
        table,
        row.id,
        mergedColumnVisibility,
      );
      const filterValueTranslationKey = readEnumFilterTranslationKey(
        column?.columnDef.meta,
      );
      return {
        columnId: row.id,
        value: row.value as FilterValue,
        headerTranslationKey,
        filterValueTranslationKey,
        hidden,
      };
    });

export const activeFiltersHasHiddenTarget = <T>(
  table: Table<T>,
  rawFilters: ColumnFiltersState,
  mergedColumnVisibility?: VisibilityState,
): boolean =>
  buildActiveFilterDescriptors(table, rawFilters, mergedColumnVisibility).some(
    (d) => d.hidden,
  );

/**
 * When the column exists and may be hidden via the table API, returns controls for the
 * active-filter summary eye toggle (show / hide column).
 */
export const resolveActiveFilterColumnVisibilityToggle = <T>(
  table: Table<T>,
  columnId: string,
): { isVisible: boolean; onToggle: () => void } | undefined => {
  const column = table.getColumn(columnId);
  if (!column?.getCanHide()) {
    return undefined;
  }
  return {
    isVisible: column.getIsVisible(),
    onToggle: () => {
      const col = table.getColumn(columnId);
      if (!col?.getCanHide()) {
        return;
      }
      col.toggleVisibility(!col.getIsVisible());
    },
  };
};

/** i18n key for the filter operator label (`table.filters.*`). */
export const filterTypeLabelKey = (
  filterType: FilterType | undefined,
): string => {
  const ft = filterType ?? FilterType.IncludesString;
  switch (ft) {
    case FilterType.IncludesString:
      return "table.filters.includesString";
    case FilterType.ExcludesString:
      return "table.filters.excludesString";
    case FilterType.EqualsString:
      return "table.filters.equalsString";
    case FilterType.NotEquals:
      return "table.filters.notEquals";
    case FilterType.LessThan:
      return "table.filters.lessThan";
    case FilterType.GreaterThan:
      return "table.filters.greaterThan";
    case FilterType.LessThanOrEqual:
      return "table.filters.lessThanOrEqual";
    case FilterType.GreaterThanOrEqual:
      return "table.filters.greaterThanOrEqual";
    case FilterType.IsEmpty:
      return "table.filters.isEmpty";
    case FilterType.IsNotEmpty:
      return "table.filters.isNotEmpty";
    default: {
      return "table.filters.includesString";
    }
  }
};

/**
 * Display string for a filter value. When **`filterValueTranslationKey`** is set (enum / `EnumFilter`
 * column), uses the same `t(\`${key}.${value}\`)` pattern as the filter, including ` OR `-joined
 * multi-select values.
 */
const formatActiveFilterValuePart = (
  t: TFunction,
  raw: unknown,
  filterValueTranslationKey?: string,
): string => {
  if (raw === undefined || raw === "") {
    return "";
  }

  if (filterValueTranslationKey) {
    if (typeof raw === "boolean") {
      return t(`${filterValueTranslationKey}.${String(raw)}`);
    }
    if (typeof raw === "string") {
      const parts = processFilterValue(raw);
      if (parts.length === 0) {
        return "";
      }
      return parts
        .map((part) => t(`${filterValueTranslationKey}.${part}`))
        .join(` ${FilterOperator.OR} `);
    }
  }

  if (typeof raw === "boolean") {
    return formatBoolean(raw, t);
  }
  return String(raw);
};

/**
 * User-facing operator + value summary for an active filter chip or list row.
 */
export const formatActiveFilterSummaryLine = (
  t: TFunction,
  fv: FilterValue,
  filterValueTranslationKey?: string,
): string => {
  const operator = t(filterTypeLabelKey(fv.filter));
  if (isReadOnlyFilterType(fv.filter)) {
    return operator;
  }
  const valuePart = formatActiveFilterValuePart(
    t,
    fv.value,
    filterValueTranslationKey,
  );
  return valuePart ? `${operator}: ${valuePart}` : operator;
};
